import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, LogOut, Lock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      onSuccess(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    "w-full bg-transparent border-b border-white/20 focus:border-[#FF5A00] outline-none py-4 text-neutral-100 placeholder:text-neutral-600 transition-colors duration-300";

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="w-full max-w-md border border-white/10 p-8 md:p-10"
      data-testid="admin-login-form"
    >
      <div className="flex items-center gap-3 mb-8">
        <Lock className="w-5 h-5 text-[#FF5A00]" />
        <h2 className="font-syne font-bold text-2xl">Admin access</h2>
      </div>
      <div className="flex flex-col gap-6">
        <div>
          <label htmlFor="admin-email" className="text-xs uppercase tracking-widest text-neutral-500">
            Email
          </label>
          <input
            id="admin-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@fenrion.ai"
            className={fieldClass}
            data-testid="admin-email-input"
          />
        </div>
        <div>
          <label htmlFor="admin-password" className="text-xs uppercase tracking-widest text-neutral-500">
            Password
          </label>
          <input
            id="admin-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className={fieldClass}
            data-testid="admin-password-input"
          />
        </div>
        {error && (
          <p className="text-sm text-red-400" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          className="self-start flex items-center gap-2 bg-[#FF5A00] text-black font-medium px-8 py-3.5 rounded-full hover:bg-[#FFB800] disabled:opacity-60 transition-colors duration-300"
          data-testid="admin-login-submit"
        >
          {loading ? (
            <>
              Signing in
              <Loader2 className="w-4 h-4 animate-spin" />
            </>
          ) : (
            "Sign in"
          )}
        </button>
      </div>
    </motion.form>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadEnquiries = () => {
    setLoading(true);
    axios
      .get(`${API}/contact`, { withCredentials: true })
      .then((res) => setEnquiries(res.data))
      .catch(() => setEnquiries([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => {
        setUser(res.data);
        loadEnquiries();
      })
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
    setEnquiries([]);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-neutral-100 font-outfit px-6 md:px-16 py-16" data-testid="admin-page">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between">
          <a href="/" className="text-[#FF5A00] text-sm tracking-widest uppercase" data-testid="admin-back-link">
            &larr; Back to site
          </a>
          {user && (
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-neutral-400 hover:text-[#FF5A00] transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          )}
        </div>

        {checking ? (
          <div className="flex justify-center py-32" data-testid="admin-loading">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF5A00]" />
          </div>
        ) : !user ? (
          <div className="flex justify-center pt-20">
            <LoginForm
              onSuccess={(u) => {
                setUser(u);
                loadEnquiries();
              }}
            />
          </div>
        ) : (
          <>
            <h1 className="font-syne text-4xl md:text-5xl font-bold mt-10 mb-2" data-testid="admin-title">
              Enquiries
            </h1>
            <p className="text-neutral-500 mb-12" data-testid="admin-count">
              {loading ? "Loading..." : `${enquiries.length} total — signed in as ${user.email}`}
            </p>
            <div className="border border-white/10" data-testid="admin-enquiries-table">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 text-xs uppercase tracking-widest text-neutral-500">
                <span className="col-span-2">Name</span>
                <span className="col-span-3">Email</span>
                <span className="col-span-2">Service</span>
                <span className="col-span-3">Message</span>
                <span className="col-span-2">Received</span>
              </div>
              {enquiries.map((e) => (
                <div
                  key={e.id}
                  className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-5 border-b border-white/10 last:border-b-0 hover:bg-white/[0.03] transition-colors"
                  data-testid={`admin-enquiry-row-${e.id}`}
                >
                  <span className="col-span-2 font-medium">{e.name}</span>
                  <span className="col-span-3 text-neutral-400 break-all">{e.email}</span>
                  <span className="col-span-2 text-[#FFB800]">{e.service}</span>
                  <span className="col-span-3 text-neutral-400 text-sm">{e.message}</span>
                  <span className="col-span-2 text-neutral-500 text-sm">
                    {new Date(e.created_at).toLocaleString()}
                  </span>
                </div>
              ))}
              {!loading && enquiries.length === 0 && (
                <p className="px-6 py-10 text-neutral-500" data-testid="admin-empty-state">
                  No enquiries yet.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
